import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";

function pick(obj, keys, fallback = "") {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
}

function isNumLike(v) {
  const n = Number(v);
  return Number.isFinite(n);
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatDMY(val) {
  if (!val) return "";

  const s = String(val).trim();
  const onlyDate = s.slice(0, 10);

  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(onlyDate);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return s;

  return s;
}

function detectSourceId(r) {
  const direct = pick(r, [
    "sourceHeaderId",
    "source_header_id",
    "sourceId",
    "source_id",
    "crush1HeaderId",
    "crush1_header_id",
    "crush1Id",
    "crush1_id",
    "sourceCrush1Id",
    "source_crush1_id",
    "sourceEntryId",
    "source_entry_id",
  ]);

  if (direct && isNumLike(direct)) return Number(direct);

  const nestedObj =
    r?.source ||
    r?.sourceRef ||
    r?.source_ref ||
    r?.crush1Source ||
    r?.crush1_source ||
    null;

  if (nestedObj) {
    const nested = pick(nestedObj, [
      "headerId",
      "header_id",
      "id",
      "sourceHeaderId",
      "source_header_id",
    ]);
    if (nested && isNumLike(nested)) return Number(nested);
  }

  for (const [k, v] of Object.entries(r || {})) {
    const key = String(k).toLowerCase();
    const looks =
      (key.includes("source") || key.includes("crush1")) &&
      (key.includes("id") || key.includes("header"));
    if (looks && isNumLike(v)) return Number(v);
  }

  return null;
}

function formatSource(r) {
  const ready = pick(r, [
    "sourceDisplay",
    "source_display",
    "sourceText",
    "source_text",
    "sourceLabel",
    "source_label",
  ]);
  if (ready) return ready;

  const srcId = detectSourceId(r);
  if (!srcId) return "-";

  const itemCode = pick(r, ["itemCode", "item_code"], "");
  const remRaw = pick(r, ["remainingKg", "remaining_kg", "remKg", "rem_kg"], "");
  const rem = isNumLike(remRaw) ? Number(remRaw) : null;

  if (itemCode && rem !== null) {
    return `#${srcId} | ${itemCode} | Rem: ${rem.toFixed(3)} KG`;
  }
  if (itemCode) return `#${srcId} | ${itemCode}`;
  return `#${srcId}`;
}

export default function Crush2_page() {
  const navigate = useNavigate();

  const [filterDate, setFilterDate] = useState("");
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openHeaderId, setOpenHeaderId] = useState(null);
  const [lines, setLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const pageGroupSize = 20;

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const role = String(currentUser?.role || "").toLowerCase();

  const isUser = role === "user";
  const isOperator = role === "operator";
  const isAdmin = role === "admin";

  const canView = isUser || isOperator || isAdmin;
  const canPrint = isUser || isOperator || isAdmin;
  const canAdd = isOperator || isAdmin;
  const canEdit = isOperator || isAdmin;
  const canDelete = isAdmin;

  useEffect(() => {
    loadHeaders();
    loadItems();
    loadSources();
  }, []);

  async function loadHeaders() {
    setLoading(true);
    try {
      const res = await api.get(`/api/crush/crush2/headers`);
      setHeaders(Array.isArray(res.data) ? res.data : []);
      setCurrentPage(1);
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Failed to load records"
      );
    } finally {
      setLoading(false);
    }
  }

  async function loadItems() {
    try {
      const res = await api.get(`/api/items?type=recycle`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setItems([]);
    }
  }

  async function loadSources() {
    try {
      const res = await api.get(`/api/crush/crush2/sources/crush1`);
      setSources(Array.isArray(res.data) ? res.data : []);
    } catch {
      setSources([]);
    }
  }

  const sourceByLineId = useMemo(() => {
    const map = new Map();
    sources.forEach((s) => {
      map.set(String(s.crush1LineId), s);
    });
    return map;
  }, [sources]);

  const itemNameByCode = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const code = pick(it, ["item_code", "itemCode", "code"], "");
      const name = pick(it, ["item_name", "itemName", "name"], "");
      if (code) map.set(String(code), name || "");
    });
    return map;
  }, [items]);

  const filteredHeaders = useMemo(() => {
    if (!filterDate) return headers;
    return headers.filter(
      (h) => String(h.productionDate || "").slice(0, 10) === filterDate
    );
  }, [headers, filterDate]);

  const totalPages = Math.ceil(filteredHeaders.length / pageSize);

  const paginatedHeaders = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredHeaders.slice(start, start + pageSize);
  }, [filteredHeaders, currentPage]);

  const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
  const startPage = currentGroup * pageGroupSize + 1;
  const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterDate]);

  function goToPage(page) {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  }

  async function viewEntry(headerId) {
    if (openHeaderId === headerId) {
      setOpenHeaderId(null);
      setLines([]);
      return;
    }

    setOpenHeaderId(headerId);
    setLines([]);
    setLinesLoading(true);

    try {
      const res = await api.get(`/api/crush/crush2/headers/${headerId}/lines`);
      setLines(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Failed to load details"
      );
      setOpenHeaderId(null);
    } finally {
      setLinesLoading(false);
    }
  }

  function printEntry(headerId) {
    window.open(`/crush-2/print/${headerId}`, "_blank");
  }

  function editEntry(headerId) {
    navigate(`/crush-2/edit/${headerId}`);
  }

  async function deleteEntry(headerId) {
    const ok = window.confirm("Are you sure you want to delete this entry?");
    if (!ok) return;

    try {
      await api.delete(`/api/crush/crush2/headers/${headerId}`);
      alert("Deleted successfully");

      if (openHeaderId === headerId) {
        setOpenHeaderId(null);
        setLines([]);
      }

      loadHeaders();
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Delete failed"
      );
    }
  }

  return (
    <div className="container-fluid" style={{ padding: 16 }}>
      <div className="card border-0 shadow-sm">
        <div className="card-body p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">Crush 2 - View Records</h2>

            {canAdd && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate("/crush-2/add")}
              >
                + Add
              </button>
            )}
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2 align-items-center">
                <div className="col-md-5">
                  <input
                    type="date"
                    className="form-control"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                  />
                </div>

                <div className="col-md-5">
                  <button type="button" className="btn btn-primary w-100">
                    Search
                  </button>
                </div>

                <div className="col-md-2 text-md-end">
                  {loading ? <span className="text-muted">Loading...</span> : null}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 110 }}>Serial No.</th>
                      <th style={{ width: 140 }}>Entry ID</th>
                      <th style={{ width: 180 }}>Date</th>
                      <th style={{ minWidth: 220 }}>Received By</th>
                      <th style={{ width: 160 }} className="text-end">
                        Total KG
                      </th>
                      <th style={{ width: 290 }} className="text-center">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedHeaders.map((r, idx) => (
                      <tr key={r.headerId ?? idx}>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td>{r.headerId}</td>
                        <td>{formatDMY(r.productionDate)}</td>
                        <td>{r.receivedBy || "-"}</td>
                        <td className="text-end fw-semibold">
                          {Number(r.grandTotalKg || 0).toFixed(2)}
                        </td>

                        <td>
                          <div className="d-flex justify-content-center gap-2 flex-wrap">
                            {canView && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => viewEntry(r.headerId)}
                              >
                                {openHeaderId === r.headerId ? "Hide" : "View"}
                              </button>
                            )}

                            {canPrint && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => printEntry(r.headerId)}
                              >
                                Print
                              </button>
                            )}

                            {canEdit && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => editEntry(r.headerId)}
                              >
                                Edit
                              </button>
                            )}

                            {canDelete && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => deleteEntry(r.headerId)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}

                    {filteredHeaders.length === 0 && !loading ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted p-4">
                          No records found
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              <div className="card-footer">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
                  <div>
                    <b>Total: {filteredHeaders.length}</b>
                  </div>

                  {totalPages > 0 && (
                    <div className="d-flex gap-1 flex-wrap justify-content-center">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Prev
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(startPage - 1)}
                        disabled={startPage === 1}
                      >
                        &laquo;
                      </button>

                      {Array.from(
                        { length: endPage - startPage + 1 },
                        (_, i) => {
                          const page = startPage + i;
                          return (
                            <button
                              key={page}
                              className={`btn btn-sm ${
                                currentPage === page
                                  ? "btn-primary"
                                  : "btn-outline-secondary"
                              }`}
                              onClick={() => goToPage(page)}
                            >
                              {page}
                            </button>
                          );
                        }
                      )}

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(endPage + 1)}
                        disabled={endPage >= totalPages}
                      >
                        &raquo;
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {openHeaderId ? (
                <div className="p-3 border-top">
                  <h6 className="mb-2">Entry Details (Entry ID: {openHeaderId})</h6>

                  {linesLoading ? (
                    <div className="text-muted">Loading details...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 60 }}>#</th>
                            <th style={{ width: 240 }}>Source (Crush 1)</th>
                            <th style={{ width: 160 }}>Machine No</th>
                            <th style={{ width: 180 }}>Operator</th>
                            <th style={{ width: 130 }}>Time/Hour</th>
                            <th style={{ width: 140 }}>Item Code</th>
                            <th style={{ minWidth: 220 }}>Item Name</th>
                            <th style={{ width: 140 }} className="text-end">
                              Remaining KG
                            </th>
                            <th style={{ width: 130 }} className="text-end">
                              Result KG
                            </th>
                            <th style={{ width: 140 }}>Receive Date</th>
                          </tr>
                        </thead>

                        <tbody>
                          {lines.map((l, i) => {
                            const sourceLineId = pick(
                              l,
                              ["sourceCrush1LineId", "source_crush1_line_id"],
                              ""
                            );
                            const sourceRow = sourceByLineId.get(String(sourceLineId));

                            const itemCode = pick(l, ["itemCode", "item_code"], "-");
                            const itemName =
                              pick(l, ["itemName", "item_name"], "") ||
                              sourceRow?.itemName ||
                              itemNameByCode.get(String(itemCode)) ||
                              "-";

                            const sourceOriginalKg = num(sourceRow?.crush1Kg);
                            const usedKg = num(
                              pick(l, ["kg", "result_kg", "resultKg"], 0)
                            );
                            const remainingKg = Math.max(
                              0,
                              sourceOriginalKg - usedKg
                            );

                            return (
                              <tr key={pick(l, ["id", "lineId", "line_id"], i) || i}>
                                <td>{i + 1}</td>
                                <td>{formatSource(l)}</td>
                                <td>{pick(l, ["machine_no", "machineNo"], "-")}</td>
                                <td>{pick(l, ["operator_name", "operatorName"], "-")}</td>
                                <td>{pick(l, ["time_hour", "timeHour", "hours"], "-")}</td>
                                <td>{itemCode}</td>
                                <td>{itemName}</td>
                                <td className="text-end fw-semibold">
                                  {remainingKg.toFixed(2)}
                                </td>
                                <td className="text-end fw-semibold">
                                  {usedKg.toFixed(2)}
                                </td>
                                <td>
                                  {formatDMY(
                                    pick(l, ["receive_date", "receiveDate"], "")
                                  ) || "-"}
                                </td>
                              </tr>
                            );
                          })}

                          {lines.length === 0 ? (
                            <tr>
                              <td colSpan="10" className="text-center text-muted">
                                No line items found
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}